import { all, call, debounce, put, select, takeLatest, takeEvery } from 'redux-saga/effects';

import {
  bulkUpdateVisualization as bulkUpdateVisualizationApi,
  updateVisualization as updateVisualizationApi,
  deleteVisualization as deleteVisualizationApi,
  createVisualization as createVisualizationApi,
  getVisualizations as getVisualizationsApi,
  getSeries,
  getLastValues,
} from '../../services';
import {
  REFRESH_VALUES,
  setPollingActive,
  setPollingActiveDone,
  getPollingDateRange,
  getRangeFromLabel,
  getRangeFromPartial,
} from '../application';
import { getSourceConnectivityTree } from '../sources';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import {
  CREATE_VISUALIZATIONS_SUBSCRIPTION,
  GET_SINGLE_VISUALIZATION_DATA,
  REQUEST_DELETE_VISUALIZATION,
  REQUEST_VISUALIZATIONS,
  SAVE_VISUALIZATION,
  LAST_VALUE_VISUALIZATIONS,
  BULK_UPSERT_VISUALIZATION,
  requestVisualizations,
  getSingleVisualization,
  deleteVisualizationSuccess,
  receiveVisualization,
  receiveVisualizations,
  receivedValues,
  setLoading,
} from './actions';
import {
  getVisualization,
  getVisualizationSubscriptions,
  getVisualizationValues,
} from './selectors';

export function* doSaveVisualization(action) {
  const { visualization, parentPanelId } = action;
  if (!visualization) return;

  try {
    let res;

    const cleanedViz = {
      ...visualization,
      variables: visualization.variables.map(({ aggregate = 'none', ...rest }) => ({
        ...rest,
        aggregate,
      })),
    };
    if (!visualization.id || visualization.id.includes('temporary')) {
      const temporaryId = visualization.id;
      delete visualization.id;
      res = yield call(createVisualizationApi, cleanedViz);
      yield put(deleteVisualizationSuccess(temporaryId));
    } else {
      res = yield call(updateVisualizationApi, visualization.id, cleanedViz);
    }

    yield put(receiveVisualization(res));
    // yeild series data - to avoid reload on select/unselect checkbox in viz panel
    if (parentPanelId) yield put(getSingleVisualization(res, parentPanelId));
    yield put(displayNotification(getNotification('saveVisualization', 'success')()));
  } catch (e) {
    console.error('Unable to save visualization: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('saveVisualization', 'error')()));
  }
}

export function* doBulkSaveVisualization(action) {
  const { visualization, siteId } = action;
  if (!visualization) return;

  try {
    yield call(bulkUpdateVisualizationApi, visualization);
    yield put(requestVisualizations(siteId));
    yield put(displayNotification(getNotification('saveVisualization', 'success')()));
  } catch (e) {
    yield call(checkOnline);
    yield put(displayNotification(getNotification('saveVisualization', 'error')()));
  }
}

export function* doDeleteVisualization(action) {
  const { visualizationId } = action;
  try {
    yield call(deleteVisualizationApi, visualizationId);
    yield put(deleteVisualizationSuccess(visualizationId));
    yield put(
      displayNotification(getNotification('deleteVisualization', 'success')(visualizationId))
    );
  } catch (e) {
    console.error('Unable to delete visualization: ', e);
    yield call(checkOnline);
    yield put(
      displayNotification(getNotification('deleteVisualization', 'error')(visualizationId))
    );
  }
}

export function* doRequestVisualizations(action) {
  const { siteId } = action;
  try {
    const { values } = yield call(getVisualizationsApi, siteId);
    yield put(receiveVisualizations(values, siteId));
  } catch (e) {
    console.error('Unable to fetch visualizations: ', e);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getVisualizations', 'error')()));
    yield put(receiveVisualizations([], siteId));
  }
}

export function* doFetchVisualizationData(visualization, panelId) {
  const { variables: rawVariables, type } = visualization || {};

  yield put(setLoading(visualization.id, panelId));

  const dateRange = yield select((state) => getPollingDateRange(state, panelId));
  const relativeAdjustedRange = dateRange?.label
    ? getRangeFromLabel(dateRange.label)
    : getRangeFromPartial(dateRange);

  try {
    const variables = (rawVariables || []).map((config) => {
      const { aggregate: aggValue, ...rest } = config;

      if (LAST_VALUE_VISUALIZATIONS.includes(type)) {
        rest.fill = null;
      }

      if (aggValue === 'none') {
        rest.aggregate = 'avg';
      } else {
        rest.aggregate = aggValue;
      }
      return rest;
    });

    const connection = yield select(getSourceConnectivityTree);

    const payload = { variables, connection };

    if (LAST_VALUE_VISUALIZATIONS.includes(type)) {
      const data = yield call(getLastValues, payload);

      yield put(receivedValues(visualization.id, { latestValues: data.values }, panelId));
    } else {
      const data = yield call(getSeries, {
        ...payload,
        from: relativeAdjustedRange.startDate,
        to: relativeAdjustedRange.endDate,
        interval: relativeAdjustedRange.granularity,
      });

      yield put(receivedValues(visualization.id, { seriesValues: data.values }, panelId));
    }
  } catch (e) {
    console.error('Unable to fetch visualization: ', e);
    yield call(checkOnline);
    yield put(
      displayNotification(
        getNotification('getVisualization', 'error')(
          visualization.id,
          typeof e.message === 'object' ? e.message?.message : e.message
        )
      )
    );
    yield put(setLoading(visualization.id, panelId, false));
  }
}

export function* doGetSingleVisualization(action) {
  const { visualizationId, visualization, panelId } = action;
  const viz = visualization || (yield select((state) => getVisualization(state, visualizationId)));
  yield call(doFetchVisualizationData, viz, panelId);
}

export function* doUpdateAllImmediately() {
  yield put(setPollingActive('visualizations'));
  const subscriptions = yield select(getVisualizationSubscriptions);
  const subscriptionIds = Object.keys(subscriptions);
  const values = yield select(getVisualizationValues);
  const visualizations = yield all(
    subscriptionIds.map((id) => select((state) => getVisualization(state, id)))
  );

  yield all(
    visualizations.flatMap((visualization) =>
      values[visualization.id].map((item) =>
        call(doFetchVisualizationData, visualization, item.panelId)
      )
    )
  );

  yield put(setPollingActiveDone('visualizations'));
}

export const sagas = [
  takeEvery(SAVE_VISUALIZATION, doSaveVisualization),
  takeLatest(BULK_UPSERT_VISUALIZATION, doBulkSaveVisualization),
  takeLatest(REQUEST_VISUALIZATIONS, doRequestVisualizations),
  takeLatest(REQUEST_DELETE_VISUALIZATION, doDeleteVisualization),

  takeEvery(CREATE_VISUALIZATIONS_SUBSCRIPTION, doGetSingleVisualization),
  takeEvery(GET_SINGLE_VISUALIZATION_DATA, doGetSingleVisualization),
  debounce(500, REFRESH_VALUES, doUpdateAllImmediately),
];
